package br.com.texeratx.boardgametimeapp.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.OpenInFull
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchController
import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchState
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.screens.active_match.ActiveMatchScreen
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.navigator.currentOrThrow
import org.koin.compose.getKoin

@Composable
fun ActiveMatchMiniPlayer(
    modifier: Modifier = Modifier
) {
    val S = LocalStrings.current
    val navigator = LocalNavigator.currentOrThrow
    val controller = getKoin().get<ActiveMatchController>()
    val state by controller.state.collectAsState()

    if (state is ActiveMatchState.Idle || state is ActiveMatchState.Finished) return

    var showFinishDialog by remember { mutableStateOf(false) }

    val (gameName, timeText, statusText, isRunning) = when (state) {
        is ActiveMatchState.Running -> {
            val s = state as ActiveMatchState.Running
            Quadruple(s.match.gameName, s.formattedElapsed, S.statusRunning, true)
        }
        is ActiveMatchState.Paused -> {
            val s = state as ActiveMatchState.Paused
            Quadruple(s.match.gameName, s.formattedElapsed, S.statusPaused, false)
        }
        is ActiveMatchState.Finished -> {
            val s = state as ActiveMatchState.Finished
            Quadruple(s.match.gameName, s.formattedElapsed, S.statusFinished, false)
        }
        else -> Quadruple(S.activeMatchFallback, "", "", false)
    }

    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.tertiaryContainer,
            contentColor = MaterialTheme.colorScheme.onTertiaryContainer
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 10.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = gameName,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = S.matchTimeStatusFormat(timeText, statusText),
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            Spacer(modifier = Modifier.width(8.dp))

            // Pause/Resume (só faz sentido para Running/Paused)
            if (state is ActiveMatchState.Running || state is ActiveMatchState.Paused) {
                IconButton(
                    onClick = { if (isRunning) controller.pause() else controller.resume() }
                ) {
                    Icon(
                        imageVector = if (isRunning) Icons.Default.Pause else Icons.Default.PlayArrow,
                        contentDescription = if (isRunning) S.pause else S.resume
                    )
                }
            }

            // Encerrar (com confirmação)
            IconButton(onClick = { showFinishDialog = true }) {
                Icon(
                    imageVector = Icons.Default.Close,
                    contentDescription = S.finishMatch
                )
            }

            // Abrir tela completa (sem duplicar)
            IconButton(
                onClick = { openOrReplaceActiveMatchScreen(navigator) }
            ) {
                Icon(
                    imageVector = Icons.Default.OpenInFull,
                    contentDescription = S.openMatch
                )
            }
        }
    }

    if (showFinishDialog) {
        FinishActiveMatchDialog(
            onDismiss = { showFinishDialog = false },
            onConfirmFinish = {
                showFinishDialog = false
                controller.finish()
                navigator.push(br.com.texeratx.boardgametimeapp.ui.screens.active_match.MatchScoreScreen())
            }
        )
    }
}

private fun openOrReplaceActiveMatchScreen(navigator: Navigator) {
    // Se já estiver na tela, não faz nada
    if (navigator.lastItemOrNull is ActiveMatchScreen) return

    // Evita empilhar telas repetidas: se já existe uma ActiveMatchScreen na pilha,
    // usa replace para manter a pilha mais limpa.
    val hasInStack = navigator.items.any { it is ActiveMatchScreen }
    if (hasInStack) {
        navigator.replace(ActiveMatchScreen())
    } else {
        navigator.push(ActiveMatchScreen())
    }
}

private val Navigator.lastItemOrNull: Screen?
    get() = runCatching { this.lastItem }.getOrNull()

// Helper para retornar 4 valores sem criar data class separada
private data class Quadruple<A, B, C, D>(val first: A, val second: B, val third: C, val fourth: D)
